import { z } from 'zod';
import { moneySchema } from './primitives.js';
/**
 * Registr klicu faktu nemovitosti (krok 16 bod 4) - JEDINE misto, kde se
 * definuje, jaka fakta system zna (fact_key, typ hodnoty, jednotka, i18n
 * label). VOLNE KLICE JSOU CHYBA: setFact() neznamy klic odmitne (422),
 * takze data v property_facts jsou vzdy interpretovatelná a reportovatelná.
 *
 * Novy klic = radek v FACT_KEYS + labely do i18n katalogu (`property.fact.*`,
 * cs+en parita). Identifikatory anglicky snake_case (CLAUDE.md §3).
 */
/** Typy hodnot jsonb sloupce `value`. */
export const FACT_VALUE_TYPES = [
    'number',
    'integer',
    'text',
    'boolean',
    'money',
    'date',
    // Krok PROP1: SEZNAM textu (vybaveni, vady). Smlouva je uz nese jako typ
    // `seznam` a klientsky dotaznik jako pole textu — bez tohoto typu by se
    // musely do faktu slepovat do jednoho stringu a nikdo by je uz nerozdelil.
    'list',
    /*
     * Krok NAB3: SEZNAM POLOZEK S POLI (zatim jen `room_list` — mistnosti bytu).
     * Mistnost neni jeden retezec: ma typ, nazev, plochu, podlahu, okna,
     * stineni a fotku a kazde z toho pole jde jinam (plocha do smlouvy,
     * `room_type` fotky na portal, podlahy a stineni do popisu v NAB5). Jako
     * `list` by z ni zbyla jedna veta, kterou uz nikdo nerozdeli — presne duvod,
     * proc v PROP1 vznikl `list` misto slepovani do textu.
     *
     * Platforma ZNA JEN TVAR (pole plochych objektu), nikoli jmena poli
     * mistnosti: sablona mistnosti je DATA kancelare
     * (`field-intake/templates/mistnost.json`), ne typ v registru.
     */
    'object_list',
];
function def(key, valueType, unit, description) {
    return { key, valueType, unit, labelKey: `property.fact.${key}`, description };
}
/** Seed registru kroku 16 - zakladni fakta nemovitosti a jednotek. */
export const FACT_KEYS = [
    def('living_area_m2', 'number', 'm2', 'Living area of the unit or building'),
    def('usable_area_m2', 'number', 'm2', 'Usable area of the unit or building'),
    def('land_area_m2', 'number', 'm2', 'Land area of the parcel or property'),
    def('built_up_area_m2', 'number', 'm2', 'Built-up area of the building footprint'),
    def('rooms_count', 'integer', null, 'Number of rooms (czech convention 1+kk, 2+1 maps here)'),
    def('floor_number', 'integer', null, 'Floor the unit is on (0 = ground floor)'),
    def('build_year', 'integer', null, 'Year the building was completed'),
    def('condition', 'text', null, 'Condition of the property (e.g. renovated, developer_project)'),
    def('ownership_type', 'text', null, 'Ownership type (e.g. personal, cooperative, state_municipal)'),
    def('energy_class', 'text', null, 'Energy performance class (A-G per EPBD)'),
    def('parking_spaces', 'integer', null, 'Number of parking spaces belonging to the property'),
    def('has_elevator', 'boolean', null, 'Whether the building has an elevator'),
    def('has_balcony', 'boolean', null, 'Whether the unit has a balcony'),
    def('has_loggia', 'boolean', null, 'Whether the unit has a loggia'),
    def('has_terrace', 'boolean', null, 'Whether the unit has a terrace'),
    def('has_cellar', 'boolean', null, 'Whether the unit has a cellar'),
    /*
     * Krok PROP3 bod 1 — CENA MA JEDEN VYZNAM NA JEDNOM MISTE.
     *
     * `asking_price` = NABIDKOVA CENA. Jediny klic, ze ktereho cte inzerat,
     * verejna stranka nemovitosti a vychozi zaklad provize — proto se do nej
     * nesmi dostat nic jineho: ani „predstava majitele"
     * (`sale.owner_price_expectation`), ani cenove pasmo z provereni (odhad
     * ceny, `market.*`). Kdyz by to kazda cesta ukladala sem, portal a verejna
     * stranka by ukazovaly jinou cenu (audit propojeni 14. 10., nalez 12).
     */
    def('asking_price', 'money', null, 'Offer price — the single price shown in the listing and on the public page'),
    def('price_note', 'text', null, 'Free-text note about the price (e.g. negotiable, includes fees)'),
    def('rent_base_amount', 'money', null, 'Base monthly rent'),
    // Market datovy balik (D11). Typ ceny nese atribut `price_type` PRIMO NA
    // FAKTU (value_json vedle amount/currency), ne dalsi namespace klicu.
    def('market.adjusted_asking', 'money', null, 'Asking price adjusted for comparability'),
    def('market.realized', 'money', null, 'Realized transaction price (contract/cadastre, never from listings)'),
    def('market.expert_value', 'money', null, 'Expert appraisal value'),
    def('market.modelled_value', 'money', null, 'Model-derived value estimate'),
    def('market.price_per_m2', 'money', null, 'Price per square meter of the relevant area'),
    def('listing.relist_count', 'integer', null, 'How many times the listing was re-published'),
    def('listing.days_on_market', 'integer', null, 'Days the listing has been on the market'),
    def('listing.status', 'text', null, 'Listing status (e.g. active, withdrawn, sold)'),
    // Krok UXV4c: nabor na miste - pevny seznam poli, ktera smi rozbor rozhovoru
    // s majitelem zapsat (vzdy s puvodem OWNER_CLAIM). Nic mimo registr.
    def('disposition', 'text', null, 'Layout as stated in Czech convention (e.g. 3+1, 2+kk, 5+1)'),
    def('heating_type', 'text', null, 'Main heating system and backup (e.g. heat pump, gas boiler)'),
    def('renovation_note', 'text', null, 'Renovations done and when (e.g. roof replaced 2019)'),
    def('sale.deadline', 'text', null, 'When the owner wants or needs to sell (owner statement)'),
    def('sale.reason', 'text', null, 'Why the owner sells (owner statement)'),
    def('sale.has_lien', 'boolean', null, 'Whether a mortgage or lien encumbers the property (owner statement)'),
    /*
     * Krok PROP3 bod 1: PREDSTAVA MAJITELE — jen informace pro maklere. Nikdy
     * se nevydava za nabidkovou cenu (`asking_price` vyse): inzerat, verejna
     * stranka ani zaklad provize z ni necte.
     */
    def('sale.owner_price_expectation', 'money', null, 'Owner price expectation — information only, never published'),
    def('sale.exclusivity', 'boolean', null, 'Whether the owner agrees to an exclusive brokerage'),
    def('sale.available_from', 'text', null, 'When the property can be vacated / handed over'),
    def('energy.monthly_costs', 'money', null, 'Monthly energy costs as stated by the owner'),
    /*
     * Krok PIL1 (vada V10 generalky GEN1): nabor na miste zapisoval „co majitel
     * tvrdi o nemovitosti", ale JMENO A SPOJENI na majitele se tam zadat nedalo
     * (snimek `GEN1/4-04`) — makléřka si je musela psat bokem.
     *
     * Je to TVRZENI MAJITELE jako kazde jine (puvod `OWNER_CLAIM`), ne strana
     * zakazky: smluvni strana vznika az v kontextu `party` s dolozenou
     * totoznosti. Jednoduchy text, zadne overovani — prepis z rozhovoru.
     */
    def('owner.contact_name', 'text', null, 'Owner name as stated during the on-site intake'),
    def('owner.contact_phone', 'text', null, 'Owner phone as stated during the on-site intake'),
    def('owner.contact_email', 'text', null, 'Owner e-mail as stated during the on-site intake'),
    /*
     * Krok WEBP3 (audit GPT 10. 10. 2026, nalezy P03/P05/P10) — ROZHODOVACI
     * UDAJE VEREJNE NABIDKY. Verejna stranka nemovitosti o nich dosud mlcela,
     * protoze pro ne registr NEMEL KLIC; kupujici se tak na vytapeni, termin
     * predani, jistotu nebo rezim DPH musel zeptat makleře telefonem.
     *
     * Zadny z nich NENI dopocet: kdyz fakt neni, stranka u povinneho pole pro
     * dany typ nabidky napise „nezjištěno" (nikdy domyslenou hodnotu).
     */
    def('renovation_year', 'integer', null, 'Year of the last renovation (scope stays in renovation_note)'),
    /*
     * Krok PROP5 — PREKONANE KLICE, ktere ted CTE alias (`FACT_KEY_ALIASES`).
     * Zustavaji v registru, protoze fakta se nemazou (pravidlo 5 CLAUDE.md):
     * stare radky `property_facts` musi zustat interpretovatelne.
     */
    def('handover_from', 'text', null, 'DEPRECATED alias of sale.available_from — when the property can be handed over'),
    def('hoa.monthly_fees', 'money', null, 'DEPRECATED alias of svj_advances_monthly — monthly advances to the HOA'),
    def('hoa.repair_fund', 'money', null, 'DEPRECATED alias of svj_contribution_monthly — monthly contribution to the repair fund'),
    def('rent.deposit', 'money', null, 'Security deposit required before moving in'),
    def('rent.commission', 'money', null, 'Brokerage commission for the lease'),
    def('rent.commission_payer', 'text', null, 'Who pays the lease commission (tenant, landlord, split)'),
    def('rent.term', 'text', null, 'Lease term as offered (e.g. 12 months, indefinite)'),
    def('rent.pets_allowed', 'boolean', null, 'Whether pets are allowed by the landlord'),
    def('rent.available_from', 'text', null, 'When the tenant can move in'),
    def('commercial.permitted_use', 'text', null, 'Permitted use of the premises per the building authority'),
    def('commercial.vat_regime', 'text', null, 'VAT regime of the rent or price (e.g. plus VAT, VAT exempt)'),
    /*
     * Krok PROP1 — UDAJ ZADANY JEDNOU SE OBJEVI VSUDE.
     *
     * Nabor, klientsky dotaznik a smlouva sbiraly tyz udaj trikrat, protoze pro
     * nej registr NEMEL KLIC (dotaznik uměl jen pole smlouvy). Co se objevuje
     * ve vic nez jedne z tech tri cest, ma od PROP1 fakt — mapovani na pole
     * smlouvy a na koncept inzeratu zije v `property-field-map.ts`.
     *
     * Jmena NOVYCH klicu jsou `id` z katalogu naboru bytu
     * (`MAKLEROV-KATALOG-NABORU-BYTU-2026-10-14.json`); stare klice se
     * neprejmenovavaji a odlisnost nese sloupec `catalogId` v mape.
     */
    def('parking_type', 'text', null, 'Kind of parking belonging to the property (none, outdoor, garage box…)'),
    def('building_roof_renovation_year', 'integer', null, 'Year the roof was renovated'),
    def('room_window_replacement_year', 'integer', null, 'Year the windows were replaced'),
    def('wiring_reconstruction_year', 'integer', null, 'Year the wiring and plumbing risers were renovated'),
    def('heating_boiler_year', 'integer', null, 'Year the heat source (boiler, heat pump) was replaced'),
    def('audit_penb_valid_until', 'date', null, 'Date until which the energy performance certificate is valid'),
    def('equipment_included', 'list', null, 'Equipment that stays in the property (annex of the contract)'),
    def('flat_defect_list', 'list', null, 'Defects as stated by the owner (seller statement)'),
    def('handover_electricity_meter', 'text', null, 'Electricity meter reading (or "at handover")'),
    def('handover_gas_meter', 'text', null, 'Gas meter reading (or "at handover")'),
    def('handover_hot_water_meter', 'text', null, 'Hot water meter reading (or "at handover")'),
    def('handover_cold_water_meter', 'text', null, 'Cold water meter reading (or "at handover")'),
    def('execution_or_insolvency', 'boolean', null, 'Whether an execution or insolvency encumbers the owner'),
    def('unit_tenanted', 'boolean', null, 'Whether tenants occupy the property'),
    /*
     * Krok NAB2 — BODY NABOROVEHO CHECKLISTU BYTU, ktere registr jeste nemel.
     *
     * Sablona `@reos/field-intake/templates/byt-prodej.json` je seznam otazek
     * naboru; KAZDA odpoved se uklada jako fakt nemovitosti, takze kazdy bod
     * sablony musi mit klic tady (test `nab2-checklist-template.spec.ts`).
     * Jmena = `id` z katalogu naboru bytu (konvence PROP1 vyse); kde uz klic
     * pro tentyz pojem existoval, bod sablony ukazuje NA NEJ a novy nevznika
     * (napr. `property_layout` -> `disposition`, `unit_floor` -> `floor_number`).
     */
    def('owner_identity_verified', 'text', null, 'How the owner identity was verified at the intake'),
    def('owner_authority', 'text', null, 'Who may dispose of the property (sole owner, co-owners, proxy…)'),
    def('cadastral_territory', 'text', null, 'Cadastral territory of the unit'),
    def('unit_number', 'text', null, 'Unit number as stated in the land registry'),
    def('land_registry_sheet', 'text', null, 'Land registry sheet (LV) number'),
    /*
     * Krok LV1fix2 bod 3 — CO Z PLACENEHO VYPISU PATRI NA KARTU.
     *
     * Zivy test 30. 10. 2026 nasel, ze z vypisu dorazila na kartu dve pole
     * (cislo LV a trida ochrany BPEJ), prestoze fakta provereni nesla vlastniky,
     * podily, datum vypisu, zastavy, plomby i nabyvaci titul. Makler tak mel
     * zaplacena data v reportu a prazdnou kartu — a smlouva z karty vyplnila
     * „bez-faktu" (nalez LV1fix2 bod 5).
     *
     * JMENA VLASTNIKU TADY JSOU, na rozdil od `lvPrefillFields`: smlouva
     * potrebuje prodavajiciho a jeho podil (`prodavajici[].jmeno|adresa|podil`)
     * a mezikrok pres kontext `party` dnes neexistuje, takze bez tohoto faktu
     * by makler jmeno z PLACENEHO vypisu prepisoval rucne. Hodnota je seznam
     * polozek (jeden vlastnik = jedna polozka) a jde na kartu JEN pres branku
     * katastru (`isKnRestrictedFact` nad `ownership.owner`), takze bez souhlasu
     * ČÚZK nebo vyjimky KNT1 nevznikne nic. RODNA CISLA se do vypisu nedostanou
     * (strukturalni redakce `assertRedacted` + `assertNoBirthNumber`).
     */
    def('land_registry_owners', 'object_list', null, 'Owners from the land registry extract — one object per owner (name, share, person kind, municipality, ico)'),
    def('land_registry_issued_on', 'date', null, 'Date the land registry extract was issued (section A of the extract)'),
    def('land_registry_plomba', 'boolean', null, 'Whether a pending land registry proceeding (plomba) is recorded for the property'),
    def('land_registry_acquisition_title', 'text', null, 'Most recent acquisition title from section E of the land registry extract'),
    /*
     * Krok ZAK1 bod 4 — PODIL NA JEDNOTCE Z LISTU VLASTNICTVI. Smlouva pro nej
     * pole ma uz od slovniku T1 (`nemovitost.jednotka-podil`), registr faktu ne,
     * takze „1/1" nebo „4523/180000" se do smlouvy psalo rucne podruhe. Hodnota
     * je ZLOMEK JAKO TEXT (ne cislo): „4523/180000" se nesmi zaokrouhlit.
     */
    def('unit_share', 'text', null, 'Ownership share of the unit as written in the land registry (fraction, e.g. 4523/180000)'),
    def('unit_designated_use', 'text', null, 'Designated use of the unit (flat, studio, non-residential)'),
    def('building_type', 'text', null, 'Structure of the building (brick, panel, skeleton…)'),
    def('apartment_condition', 'text', null, 'Condition of the unit itself (building state stays in condition)'),
    def('flat_observed_defects', 'boolean', null, 'Whether the broker saw defects in the unit'),
    def('energy_certificate_state', 'text', null, 'Availability of the energy performance certificate'),
    def('energy_marketing_class', 'text', null, 'Energy class the listing may publish'),
    def('has_garage', 'boolean', null, 'Whether a garage belongs to the property'),
    def('parking_available', 'boolean', null, 'Whether parking is secured for the property'),
    def('land_registry_checked', 'text', null, 'Whether the current land registry extract was checked'),
    def('authorization_to_sell', 'text', null, 'Whether all persons needed for the sale are authorized'),
    def('right_of_use_encumbrance', 'boolean', null, 'Whether easements or rights of use encumber the property'),
    def('third_party_rights', 'list', null, 'Third-party rights outside the land registry extract'),
    def('known_hidden_defects', 'boolean', null, 'Whether hidden or recurring defects are known'),
    def('balcony_area_m2', 'number', 'm2', 'Balcony area'),
    def('loggia_area_m2', 'number', 'm2', 'Loggia area'),
    def('terrace_area_m2', 'number', 'm2', 'Terrace area'),
    def('cellar_area_m2', 'number', 'm2', 'Cellar area'),
    /*
     * Krok NAB3 — MISTNOSTI BYTU. Jeden fakt, jedna polozka = jedna mistnost
     * (objekt podle sablony mistnosti). Souhrnna veta polozky (`summary`) je to,
     * co cte smlouva `nemovitost.mistnosti[]`; sklada ji zapisujici strana
     * z sablony (viz `PROPERTY_FIELD_MAP`).
     */
    def('room_list', 'object_list', null, 'Rooms of the unit — one object per room (type, name, area, floor, windows, shading)'),
    /*
     * Krok PROP5 — SVJ MA JEDNU SADU FAKTU (nalez 19 auditu 14. 10.).
     *
     * Tytez udaje vedly ctyri evidence: nabor (`hoa.monthly_fees`,
     * `hoa.repair_fund`), klientsky dotaznik (jen pole smlouvy `svj.*`),
     * provereni (`building_hoa.*`) a smlouva (`nemovitost.svj-nazev`). „Fond
     * oprav" pritom jednou znamenal MESICNI PRISPEVEK a jinde STAV FONDU —
     * jedna castka se tak v inzeratu objevila jako druha.
     *
     * Jmena jsou `id` z katalogu naboru bytu, skupina `svj`. Dve vyjimky nese
     * komentar u klice: nazev SVJ a stav fondu katalog jako otazku naboru nema
     * (nazev dodava rejstrik, stav fondu ucetni zaverka), presto je obe cesty
     * potrebuji pojmenovat stejne.
     *
     * ROZDIL, KTERY SE NESMI ZTRATIT:
     *   `svj_advances_monthly`     = cela mesicni platba SVJ (zalohy na sluzby),
     *   `svj_contribution_monthly` = jeji CAST na spravu domu („fond oprav"),
     *   `svj_repair_fund_balance`  = STAV fondu (jednorazovy zustatek).
     */
    def('svj_name', 'text', null, 'Name of the HOA or housing cooperative (no catalog question — comes from the public registry)'),
    def('svj_entity_id', 'text', null, 'Registration number (ICO) of the HOA or cooperative'),
    def('svj_advances_monthly', 'money', null, 'Monthly advances paid to the HOA (services)'),
    def('svj_contribution_monthly', 'money', null, 'Monthly contribution to house administration — the part known as repair fund'),
    def('svj_repair_fund_balance', 'money', null, 'Balance of the repair fund (never the monthly contribution; no catalog question — comes from the financial statements)'),
    def('svj_finance_debt', 'boolean', null, 'Whether the HOA has a loan'),
    def('svj_advances_for_people', 'integer', null, 'How many persons the advances are calculated for'),
    /*
     * Krok LV2 — VETSINA HLASU V SVJ. Podil na spolecnych castech jsou hlasy
     * (§ 1206 NOZ), takze „jeden vlastnik ma 73 %" rozhoduje o tom, jak se
     * v dome hlasuje. Pocita se SOUCTEM podilu z listu vlastnictvi pozemku pod
     * domem (`duediligence/internal/unit/majority-owner.ts`), na kartu jde
     * jako HOTOVY pomer v procentech — zlomek i jmena vlastniku zustavaji
     * faktem provereni, karta nese udaj o NEMOVITOSTI (tatáz uvaha jako
     * u `ownership.lv` → `land_registry_sheet`).
     */
    def('svj_majority_owner_percent', 'integer', '%', 'Share of the largest single owner in the building common parts, in percent (majority of votes in the HOA when above 50)'),
    /*
     * Krok NAB5 — VOLITELNE ATRIBUTY INZERATU, ktere karta nemovitosti sbirá,
     * ale registr pro ne klic nemel, takze se na portal nikdy nedostaly.
     *
     * HODNOTA JE KANONICKY KOD MALYMI PISMENY (vzor `building_type` = 'brick',
     * `ownership_type` = 'personal'), nikdy cesky popisek ani cislo portalu:
     * kazdy portal ma vlastni ciselnik (Sreality vs. RealityMIX) a prevod zije
     * v jeho mapperu. Multiselect je `list` (vzor `third_party_rights`) — jeden
     * byt ma klidne dva zdroje tepla a slepit je do textu uz nikdo nerozdeli.
     */
    def('heating_source', 'list', null, 'Heat sources (canonical codes, e.g. heat_pump, gas_boiler, district)'),
    def('heating_element', 'list', null, 'Heating elements (canonical codes, e.g. radiators, underfloor)'),
    def('hot_water_source', 'list', null, 'Hot water sources (canonical codes, e.g. electric_boiler)'),
    def('water_supply', 'list', null, 'Water supply (canonical codes, e.g. mains, well)'),
    def('gas_supply', 'list', null, 'Gas supply (canonical codes: individual, pipeline)'),
    def('electricity_supply', 'list', null, 'Electricity supply (canonical codes: v120, v230, v400, no_connection)'),
    def('circuit_breaker', 'text', null, 'Main circuit breaker rating in amperes (e.g. 25A)'),
    def('phase_count', 'text', null, 'Number of electrical phases (1 or 3)'),
    def('sewerage_type', 'list', null, 'Sewerage (canonical codes, e.g. public_sewer, septic_tank)'),
    def('internet_connection', 'list', null, 'Internet connection types (canonical codes: adsl, vdsl, fiber, wireless)'),
    def('telecom_connection', 'list', null, 'Telecom connections (canonical codes, e.g. phone, cable_tv)'),
    def('transport_access', 'list', null, 'Public transport nearby (canonical codes, e.g. train, bus)'),
    def('road_type', 'list', null, 'Access road surface (canonical codes, e.g. asphalt, unpaved)'),
    def('object_location', 'text', null, 'Where the building sits in the municipality (e.g. quiet_part, village_edge)'),
    def('surroundings_type', 'text', null, 'Character of the surrounding development (e.g. residential, rural)'),
    def('flat_class', 'text', null, 'Kind of flat (maisonette, loft, attic, single_floor)'),
    def('furnished', 'text', null, 'Whether the property is furnished (yes, no, partially)'),
    def('barrier_free', 'boolean', null, 'Whether the property has barrier-free access'),
    def('personal_transfer', 'boolean', null, 'Whether a cooperative share can be transferred to personal ownership'),
    def('floor_area_m2', 'number', 'm2', 'Floor area (total area of the unit per the listing codebook)'),
    def('garden_area_m2', 'number', 'm2', 'Garden area belonging to the property'),
    def('floors_count', 'integer', null, 'Number of above-ground floors of the building'),
    /*
     * Krok NAB5 bod 4 — PREDAVACI PROTOKOL potrebuje u meridla DVE veci: CISLO
     * (vyrobni cislo, zapisuje se uz pri naboru — je na meridle videt) a STAV
     * (odecet, ten vznika AZ PRI PREDANI, proto `deferLater` u sablony).
     * Do NAB5 existoval jen stav (`handover_*_meter`), takze cislo meridla se
     * do protokolu psalo rucne, i kdyz ho makler na prohlidce vyfotil.
     */
    def('handover_electricity_meter_number', 'text', null, 'Electricity meter serial number'),
    def('handover_gas_meter_number', 'text', null, 'Gas meter serial number'),
    def('handover_hot_water_meter_number', 'text', null, 'Hot water meter serial number'),
    def('handover_cold_water_meter_number', 'text', null, 'Cold water meter serial number'),
    /*
     * Krok NAB6 — NABOR NAJMU. Co uz najem mel (`rent.deposit`, `rent.term`,
     * `rent.pets_allowed`, `rent_base_amount`, `furnished`,
     * `equipment_included`) zustava; pridavaji se jen tri udaje, na ktere se
     * makler pri prohlidce pta a registr pro ne klic nemel. Sluzby jsou
     * ZAMERNE vlastni klic, ne `svj_advances_monthly`: zalohy SVJ plati
     * VLASTNIK domu, kdezto tady jde o to, co mesicne plati NAJEMCE vedle
     * najemneho — jedna castka ve dvou rolich byla presne nalez 19 auditu.
     */
    def('rent.service_charges', 'money', null, 'Monthly service charges the tenant pays on top of the base rent'),
    def('rent.smoking_allowed', 'boolean', null, 'Whether the landlord allows smoking in the property'),
    def('rent.max_occupants', 'integer', null, 'How many persons the landlord accepts in the property'),
    /*
     * Krok NAB6 — ROZSIRUJICI VETEV DOMU (katalog kap. 3). Byt a dum sdili
     * vetsinu bodu (`usable_area_m2`, `building_type`, `condition`,
     * `has_garage`, `sewerage_type`, `water_supply`, `floors_count`,
     * `land_area_m2`, `garden_area_m2`, `building_roof_renovation_year`) —
     * tady jsou JEN udaje, ktere byt nema. Prefix `house_` je z katalogu.
     *
     * `house_kind` a `house_object_type` nejsou kosmetika: Sreality je
     * u kategorie Domy vyzaduje (`advert_subtype`, `object_type` v tabulce 1
     * `validator.ts`) a dispozice bytu je nenahradi.
     */
    def('house_kind', 'text', null, 'Kind of house (family, villa, cottage, farmstead…) — drives advert_subtype'),
    def('house_object_type', 'text', null, 'Whether the house is single-storey or multi-storey'),
    def('house_room_count', 'integer', null, 'Number of rooms of the house (portal size attribute)'),
    def('house_pool', 'boolean', null, 'Whether a pool belongs to the house'),
    def('house_fencing', 'text', null, 'Fencing of the plot (none, partial, full)'),
    def('house_well_type', 'text', null, 'Type of well on the plot (drilled, dug)'),
    def('house_boiler_room', 'boolean', null, 'Whether the house has a separate boiler room'),
    def('house_roof_type', 'text', null, 'Roof shape and covering as seen at the intake'),
    def('house_roof_truss_condition', 'text', null, 'Condition of the roof truss as seen at the intake'),
    /*
     * Krok ZAK1 bod 2 — POLOHA Z OTEVRENYCH MAPOVYCH VRSTEV.
     *
     * Udaje, ktere REOS uz cte pri provereni (radon z CGS, zaplavy z VUV,
     * vyska z DMR 5G, bonita z VUMOP, ochranna pasma z DTM), ale registr faktu
     * pro ne klic nemel — makler je proto videl jen v reportu provereni a do
     * zakazky si je prepisoval rucne.
     *
     * KAZDY Z NICH STOJI NA JEDNOM FAKTU PROVERENI. Udaje, jejichz odpoved se
     * sklada z VIC faktu (uzemni plan: `upPlanStateFrom`; hluk: mapovanost +
     * hodnota), tady ZAMERNE klic nemaji — druha, slabsi pravda vedle SSOT
     * ctecky je presne to, co zakazuje CLAUDE.md §8 bod 2. Duvod je v reportu
     * kroku ZAK1.
     *
     * HODNOTA JE JMENOVKA ZDROJE, ne nas kod: „střední" u radonu je stupen
     * ceske mapy radonoveho indexu a prelozit ho na `medium` by znamenalo
     * vyrobit ciselnik, ktery zdroj nezna (tytez uvaha jako u `CONSTRUCTION_KINDS`).
     */
    def('radon_index', 'text', null, 'Radon index of the subsoil as published by the Czech Geological Survey'),
    def('flood_zone', 'boolean', null, 'Whether the property lies in the Q100 flood zone (VUV flood zone layer)'),
    def('terrain_elevation_m', 'number', 'm', 'Elevation above sea level from the DMR 5G terrain model'),
    def('bpej_protection_class', 'text', null, 'Agricultural soil protection class derived from the BPEJ code (decree 48/2011)'),
    def('protection_zones', 'list', null, 'Protection zones that reach the property (road, power line, water main…)'),
    /*
     * Krok ZAK1b bod 2 — PARCELA A POLOHA JAKO FAKTA KARTY.
     *
     * Do ZAK1b mel pozemek na karte nemovitosti NULA predvyplnenych udaju:
     * parcela (cislo, vymera, druh, zpusob vyuziti) i poloha (uzemni plan,
     * svazitost, bonita, site) zily jen ve faktech provereni, odkud je cetl
     * report — karta, nabor, inzerat a smlouva o nich nevedely.
     *
     * PARCELA JE SEZNAM, NE JEDEN UDAJ. Predmet zakazky ma klidne tri parcely
     * (zlaty dum Petrovice 475: st. 597, 1904/12, 1907/23) a kazda ma vlastni
     * cislo, vymeru i druh pozemku. Jako ctyri samostatne klice by se posledni
     * parcela prepsala pres predchozi — proto `object_list`, tedy tentyz tvar,
     * jaky uz nese `room_list`. Jmena poli drzi projekce v `duediligence`
     * (SSOT je `subjectParcelsOf`), registr zna jen tvar.
     *
     * `land_area_m2` tim NEZANIKA: to je pozemek PREDMETU (u bytu zadny),
     * kdezto `parcels` je vycet parcel z katastralni mapy RUIAN — u bytu
     * a domu „pozemek domu", u pozemku predmet sam.
     */
    def('parcels', 'object_list', null, 'Parcels of the property from the RUIAN cadastral map (number, area, land type, land use, zoning area)'),
    def('cadastral_territory_code', 'integer', null, 'RUIAN code of the cadastral territory (layer 7)'),
    def('zoning_function_area', 'text', null, 'Functional area code the property falls into per the municipal zoning plan'),
    def('zoning_plan_name', 'text', null, 'Name of the effective zoning plan as recorded in the UPD register'),
    def('terrain_slope_deg', 'number', 'deg', 'Terrain slope of the plot computed from the DMR 5G elevation model'),
    def('bpej_code', 'text', null, 'BPEJ code of the agricultural soil unit (VUMOP)'),
    def('utilities_available', 'list', null, 'Utilities available at the plot (water, sewer, electricity, gas, telecom)'),
];
/**
 * Krok PROP5 — PREKONANY KLIC -> KANONICKY. Zapis uz chodi jen do
 * kanonickeho klice; cteni stareho se na nej PREVADI, takze data zapsana
 * pred PROP5 nezmizi a nic se nemaze (migrace `0048` kontextu property
 * založí kanonicke radky z historickych).
 *
 * `handover_from` vs `sale.available_from` (nalez 21): „termin predani" na
 * verejne strance a „volne od" v inzeratu byl tentyz udaj ve dvou klicich —
 * makler ho zadal dvakrat a stranka ukazala ten, ktery zrovna nemel hodnotu.
 */
export const FACT_KEY_ALIASES = {
    'hoa.monthly_fees': 'svj_advances_monthly',
    'hoa.repair_fund': 'svj_contribution_monthly',
    handover_from: 'sale.available_from',
};
/** Kanonicky klic faktu (pro neznamy i uz kanonicky vraci vstup). */
export function canonicalFactKey(key) {
    return FACT_KEY_ALIASES[key] ?? key;
}
/** Prekonane klice, ktere se na dany kanonicky prevadeji (pro `fact_key = any(...)`). */
export function deprecatedFactKeysFor(canonical) {
    return Object.entries(FACT_KEY_ALIASES)
        .filter(([, target]) => target === canonical)
        .map(([key]) => key);
}
/** Seznam klicu rozsireny o jejich prekonane varianty — jeden dotaz precte oboje. */
export function factKeysWithAliases(keys) {
    const out = new Set(keys);
    for (const key of keys)
        for (const old of deprecatedFactKeysFor(key))
            out.add(old);
    return [...out];
}
/**
 * Mapa faktu podle klice, kde PREKONANY klic plni i kanonicky (a naopak),
 * takze cteni nemusi znat historii jmen. Kanonicky zapis ma prednost —
 * prekonany klic nikdy neprebije hodnotu, kterou uz nekdo zapsal nove.
 */
export function indexFactsByKey(entries) {
    const out = new Map();
    const fromAlias = new Map();
    for (const [key, value] of entries) {
        out.set(key, value);
        const canonical = canonicalFactKey(key);
        if (canonical !== key)
            fromAlias.set(canonical, value);
    }
    for (const [canonical, value] of fromAlias)
        if (!out.has(canonical))
            out.set(canonical, value);
    for (const [old, canonical] of Object.entries(FACT_KEY_ALIASES)) {
        const value = out.get(canonical);
        if (value !== undefined && !out.has(old))
            out.set(old, value);
    }
    return out;
}
/**
 * Atribut `price_type` cenoveho faktu (D11): uklada se v hodnote faktu vedle
 * amount/currency, NE jako dalsi namespace fact_key (drobne K rozhodnuti
 * v DECISIONS-BEFORE-R3: mene klicu). REALIZED nikdy nevznika z inzerce.
 */
export const PRICE_TYPES = [
    'ASKING',
    'ADJUSTED_ASKING',
    'REALIZED',
    'EXPERT_VALUE',
    'MODELLED_VALUE',
];
const FACT_KEY_INDEX = new Map(FACT_KEYS.map((entry) => [entry.key, entry]));
/** Neznamy fact_key = domena chyba (mapuje na 422 problem+json). */
export class FactKeyUnknownError extends Error {
    code = 'FACT_KEY_UNKNOWN';
    factKey;
    constructor(factKey) {
        super(`FactKeyUnknownError: neznamy fact_key '${factKey}' - registruj ho ve FACT_KEYS`);
        this.name = 'FactKeyUnknownError';
        this.factKey = factKey;
    }
}
export class FactValueInvalidError extends Error {
    code = 'FACT_VALUE_INVALID';
    factKey;
    constructor(factKey, detail) {
        super(`FactValueInvalidError: hodnota faktu '${factKey}' neodpovida typu (${detail})`);
        this.name = 'FactValueInvalidError';
        this.factKey = factKey;
    }
}
/** Definice podle klice; undefined pro neznamy klic. */
export function getFactKey(key) {
    return FACT_KEY_INDEX.get(key);
}
/** Definice nebo FactKeyUnknownError - volani setFact() musi projit registrem. */
export function requireFactKey(key) {
    const definition = FACT_KEY_INDEX.get(key);
    if (definition === undefined)
        throw new FactKeyUnknownError(key);
    return definition;
}
/** Zod schema pro hodnotu daneho klice (jsonb payload). */
export function factValueSchema(key) {
    switch (requireFactKey(key).valueType) {
        case 'number':
            return z.number().finite();
        case 'integer':
            return z.number().int();
        case 'text':
            return z.string().min(1).max(500);
        case 'boolean':
            return z.boolean();
        case 'money':
            // Money = numeric string + ISO 4217 (CLAUDE.md §3), nikdy number.
            return moneySchema;
        case 'date':
            return z.iso.date();
        case 'list':
            // Strop 50 x 200 znaku (zadani PROP1): vybaveni a vady jsou vyctem
            // polozek, ne volnym textem — delsi polozka je chyba vstupu, ne fakt.
            return z.array(z.string().min(1).max(200)).max(50);
        case 'object_list':
            /*
             * Krok NAB3 — PLOCHY objekt, zadne vnorene stromy. Klic je identifikator
             * pole sablony (snake_case), hodnota skalar nebo seznam textu (fotky,
             * vady v mistnosti). Strop 40 polozek = byt 6+ i s komorami a lodzii.
             *
             * Registr NEVALIDUJE jmena poli: ta zije v sablone mistnosti jako DATA
             * (`field-intake/templates/mistnost.json`) a kopie seznamu tady by se
             * s ni rozesla (CLAUDE.md §8 bod 2). Shodu sablony s faktem hlida
             * `nab3-mistnosti.spec.ts` v kontextu naboru.
             */
            return z
                .array(z.record(z.string().regex(/^[a-z][a-z0-9_]{0,39}$/), z.union([
                z.string().max(300),
                z.number().finite(),
                z.boolean(),
                z.null(),
                z.array(z.string().min(1).max(300)).max(20),
            ])))
                .max(40);
    }
}
/** Validuje hodnotu proti registru; pri poruseni FactValueInvalidError. */
export function validateFactValue(key, value) {
    const parsed = factValueSchema(key).safeParse(value);
    if (!parsed.success) {
        const issue = parsed.error.issues[0];
        throw new FactValueInvalidError(key, issue === undefined ? 'unknown' : `${issue.path.join('.') || '(root)'}: ${issue.message}`);
    }
}
/** Seznam klicu (pro UI/AI registry nastroju). */
export function listFactKeys() {
    return FACT_KEYS;
}
//# sourceMappingURL=fact-keys.js.map